/**
 * Dates, without a date library.
 *
 * Everything here works in a **calendar date** — `YYYY-MM-DD` — and never in a
 * `Date` for anything a person picks. A `Date` carries a time and a zone, and the
 * day somebody chose on a form is none of the platform's business until it is
 * combined with a time and a timezone on the way to the server.
 *
 * That separation is the product's own rule, from the wellness module: *"Tuesday's
 * water must not move when somebody flies to Toronto, so nothing here derives a
 * date from `now()`."* The same discipline, one layer up.
 */

export type CalendarDate = string // YYYY-MM-DD

export const toCalendarDate = (d: Date): CalendarDate =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const fromCalendarDate = (s: CalendarDate): Date => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, d ?? 1)
}

export const todayCalendar = (): CalendarDate => toCalendarDate(new Date())

/** "21 September 2026", in the reader's locale. */
export const longDate = (s: CalendarDate) =>
  fromCalendarDate(s).toLocaleDateString('en-CA', { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * "Mon" — the weekday, for the columns of 9.1's week.
 *
 * The chart labelled each bar with `day.slice(0, 1)`, and `day` is
 * `2026-09-21`, so all seven columns read **2**. It looked like a design
 * choice from a distance, which is why nobody caught it.
 */
export const weekdayShort = (s: CalendarDate) =>
  fromCalendarDate(s).toLocaleDateString('en-CA', { weekday: 'short' })

/** "21 Sep" — for a row in a table. */
export const shortDate = (s: CalendarDate) =>
  fromCalendarDate(s).toLocaleDateString('en-CA', { day: 'numeric', month: 'short' })

/** The days of a month's grid, padded to whole weeks, Monday first. */
export function monthGrid(year: number, month: number): (CalendarDate | null)[] {
  const first = new Date(year, month, 1)
  const days = new Date(year, month + 1, 0).getDate()
  // getDay() is Sunday-first; the product is Canadian and the boards start Monday.
  const lead = (first.getDay() + 6) % 7
  const cells: (CalendarDate | null)[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= days; d++) cells.push(toCalendarDate(new Date(year, month, d)))
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

/**
 * "September 2026" — the calendar's heading, when it had one.
 *
 * Kept because the heading came back as two controls rather than a label —
 * a month and a year somebody can jump to — and this is what the label said.
 * Nothing draws it today. It is two lines and the day somebody wants a month
 * written out, it is here rather than invented again.
 */
export const monthName = (year: number, month: number) =>
  new Date(year, month, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })

/** Monday-first initials, for the grid's header. */
export const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

/**
 * A time of day as minutes since midnight, which is what a picker holds.
 *
 * 9.6a: *"The start time is its own field. It was folded into the date, and the
 * field showed only the half before the comma and saved that back, so opening a
 * class and saving it unchanged threw the time off the row."* Kept separate here
 * so that cannot recur, and combined only at the edge.
 */
export const minutesToTime = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

export const timeToMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

/** "2:30 pm" — how a time is read, as against how it is stored. */
export const readableTime = (t: string) => {
  const [h, m] = t.split(':').map(Number)
  const d = new Date(2000, 0, 1, h ?? 0, m ?? 0)
  return d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
}

/**
 * A calendar date and a time of day, joined into the instant the API wants.
 *
 * The one place the two halves meet. Everything above keeps them apart.
 */
export const toInstant = (date: CalendarDate, time: string): string => {
  const [y, mo, d] = date.split('-').map(Number)
  const [h, mi] = time.split(':').map(Number)
  return new Date(y!, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0).toISOString()
}

/** And apart again, for a form that is editing something that exists. */
export const splitInstant = (iso: string): { date: CalendarDate; time: string } => {
  const d = new Date(iso)
  return {
    date: toCalendarDate(d),
    time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
  }
}

/**
 * A moment as somebody would write it down: "3 Oct 2026, 11:42 a.m." — or, with
 * `seconds`, to the second and naming the zone, for a record somebody may have to quote
 * (the activity log, client 2026-10-03: "a proper timestamp, not eight hours ago").
 * Read on the reader's own clock.
 */
export const exactTime = (iso: string, seconds = false): string =>
  new Date(iso).toLocaleString('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' as const, timeZoneName: 'short' as const } : {}),
  })
